import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const defaultDateFrom = () => {
  const now = new Date()
  return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1))
}

const defaultDateTo = () => toInputDate(new Date(Date.now() - 86400000))

export default function MasterStockList({
  title,
  description,
  labelHeader,
  endpoint,
  useRepository,
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || defaultDateFrom()
  const dateTo = searchParams.get("date_to") || defaultDateTo()
  // Nilai filter outlet: sentinel grup (GRP_ALL/GRP_HO) atau id outlet tunggal.
  const outletFilter = searchParams.get("outlet_ids") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletFilter)
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"
  // 250726 (permintaan user): satuan tampilan (PCS/LUSIN/PASANG) — angka dikonversi backend.
  const unit = (searchParams.get("unit") || "PCS").toUpperCase()
  // 280726 (permintaan user): filter gudang import/lokal + BCG — sama seperti
  // laporan Sales Qty Setahun. Berlaku utk 4 varian (global/detail, qty/nilai).
  const gudang = (() => {
    const g = (searchParams.get("gudang") || "ALL").toUpperCase()
    return g === "IMPORT" || g === "LOKAL" ? g : "ALL"
  })()
  const bcg = (() => {
    const b = (searchParams.get("bcg") || "ALL").toUpperCase()
    if (b === "1" || b === "BCG") return "BCG"
    if (b === "NON_BCG") return "NON_BCG"
    return "ALL"
  })()
  const search = searchParams.get("search") || ""
  const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })

  const { data: reportData, isLoading } = useRepository({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_ids: resolvedOutletCsv || undefined,
    unit: unit !== "PCS" ? unit : undefined,
    gudang: gudang !== "ALL" ? gudang : undefined,
    bcg: bcg !== "ALL" ? bcg : undefined,
    page,
    per_page: perPage,
    search: search || undefined,
    enabled: true,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.rows ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")
        if (pageNumber) nextParams.set("page", pageNumber)
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Tanggal dari dan sampai wajib diisi sebelum ekspor")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    })
    if (resolvedOutletCsv) query.set("outlet_ids", resolvedOutletCsv)
    if (search) query.set("search", search)
    if (unit && unit !== "PCS") query.set("unit", unit)
    if (gudang !== "ALL") query.set("gudang", gudang)
    if (bcg !== "ALL") query.set("bcg", bcg)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}${endpoint}/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">{title}</h1>
          <p className="mt-1 text-sm text-neutral-500">{description}</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-[240px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Cari Barang
            </label>
            <input
              type="text"
              value={searchTemp}
              onChange={(event) => setSearchTemp(event.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
              placeholder="Nama barang"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            />
          </div>

          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletFilter}
              onChange={(v) => mergeSearchParams({ outlet_ids: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Dari
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Sampai
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[130px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Satuan
            </label>
            <select
              value={unit}
              onChange={(event) => mergeSearchParams({ unit: event.target.value === "PCS" ? null : event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {["PCS", "LUSIN", "PASANG"].map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[140px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Gudang
            </label>
            <select
              value={gudang}
              onChange={(event) => mergeSearchParams({ gudang: event.target.value === "ALL" ? null : event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              <option value="ALL">Semua Gudang</option>
              <option value="IMPORT">Import</option>
              <option value="LOKAL">Lokal</option>
            </select>
          </div>

          <div className="min-w-[140px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Produk
            </label>
            <select
              value={bcg}
              onChange={(event) => mergeSearchParams({ bcg: event.target.value === "ALL" ? null : event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              <option value="ALL">Semua</option>
              <option value="BCG">BCG</option>
              <option value="NON_BCG">Non BCG</option>
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Page
            </label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Baris</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_rows)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Awal</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_awal)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Masuk</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_masuk)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Keluar</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_keluar)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Akhir</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_akhir)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700 w-[260px] whitespace-normal break-words leading-5">{labelHeader}</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">AWAL</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">MASUK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">KELUAR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">RETUR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">KODIN</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">KODOUT</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">TUKARAN</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">AKHIR</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data report...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada data barang untuk periode yang dipilih.
                  </td>
                </tr>
              )}

              {!isLoading && rows.map((row, index) => (
                <tr key={row.row_key || `${row.label}-${index}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                  <td className="px-4 py-3 text-neutral-800 w-[260px] whitespace-normal break-words leading-5">{row.label || "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.awal)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.masuk)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.keluar)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.retur)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.kodin)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.kodout)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.tukaran)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(row.akhir)}</td>
                </tr>
              ))}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900">GRAND TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_awal)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_masuk)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_keluar)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_retur)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_kodin)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_kodout)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_tukaran)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_akhir)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}
